import React from 'react';
import { CircleImage, IrisCircle } from '../profile/fields';
import { Icon } from '../onboarding/onboardingUi';

/**
 * A · the conditional "finish your resume" notice, on the Portal surface
 * (design_handoff_onboarding_v2, "Resume Notice v2"). Copy is fixed — this one has no admin
 * composer; `notice` carries the eyebrow, headline, body and progress from the server
 * (`AppNoticeService.BuildResumeNotice`).
 *
 * The photo row leads because a photo is the one thing the page cannot do without, and it shows
 * the photo back once there is one — the row is a status, not a second uploader. Everything the
 * row and the CTA open is `/onboard`, which is where a photo is actually chosen and framed; the
 * alternative was a second upload-and-crop path that would drift from step 1's.
 */
const STEPS = [
  { n: 2, title: 'VIBE CHECK', body: 'One line. What you are like at 9pm.', tint: '#A8D8EA' },
  { n: 3, title: 'DOWN FOR', body: 'Three things you would actually say yes to.', tint: '#C6B8FF' },
];

export default function ResumeNotice({ notice, photoUrl, onPrimary, onSecondary }) {
  const hasPhoto = !!photoUrl;
  const done = notice.stepsDone ?? 0;
  const total = notice.stepsTotal ?? 5;

  return (
    <div className="flex flex-col gap-[18px] p-[18px] pb-[22px] sm:p-[26px] sm:pb-7">
      <div className="flex items-center justify-between gap-3">
        <span
          className="flex h-[30px] items-center rounded-full px-3.5 font-mono text-[10px] font-extrabold tracking-[0.16em] text-black"
          style={{ background: 'linear-gradient(90deg, #FF4D6D, #FF7A9A)' }}
        >
          {notice.eyebrow}
        </span>
        <button
          type="button"
          onClick={onSecondary}
          aria-label="Close"
          className="pk-btn flex h-10 w-10 flex-shrink-0 items-center justify-center text-white"
          style={{ background: '#1F1F1F' }}
        >
          {Icon.close}
        </button>
      </div>

      <div className="flex h-1 gap-1" aria-hidden>
        {Array.from({ length: total }, (_, i) => (
          <span key={i} className="flex-1 rounded-full" style={{ background: i < done ? '#22ECFB' : '#2A2A2A' }} />
        ))}
      </div>

      {/* Sentence case — never inherit an uppercase h2 rule. */}
      <h2 className="text-[25px] font-bold leading-[1.12] tracking-[-0.01em] text-white sm:text-[30px]" style={{ textTransform: 'none' }}>
        {notice.headline}
      </h2>
      <p className="text-[14px] leading-[1.6]" style={{ color: '#8A8A8A' }}>{notice.body}</p>

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onPrimary}
          className="flex items-center gap-3.5 rounded-[22px] p-3.5 text-left"
          style={{ background: '#000', boxShadow: `inset 0 0 0 1px ${hasPhoto ? '#22ECFB' : '#2A2A2A'}` }}
        >
          <IrisCircle size={64} border={2} fill="#000">
            {hasPhoto ? <CircleImage src={photoUrl} inset={2} alt="" /> : (
              <span className="absolute inset-[2px] flex items-center justify-center rounded-full text-white" style={{ background: '#161616' }}>
                {Icon.plus(22)}
              </span>
            )}
          </IrisCircle>
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="flex items-center gap-2 font-mono text-[12px] font-extrabold tracking-[0.14em] text-white">
              ADD A PHOTO
              {hasPhoto && <span className="rounded-full px-2 py-[3px] text-[9px] tracking-[0.14em] text-black" style={{ background: '#22ECFB' }}>DONE</span>}
            </span>
            <span className="text-[13px] leading-[1.45]" style={{ color: '#8A8A8A' }}>
              {hasPhoto ? 'Frame it and add a few more.' : 'People invite faces, not initials.'}
            </span>
          </span>
          <span className="pk-btn h-10 flex-shrink-0 px-4 font-mono text-[10px] font-extrabold tracking-[0.14em]" style={{ background: hasPhoto ? '#1F1F1F' : '#fff', color: hasPhoto ? '#fff' : '#000' }}>
            {hasPhoto ? 'CHANGE' : 'ADD'}
          </span>
        </button>

        {STEPS.map((s) => (
          <div key={s.n} className="flex items-center gap-3.5 rounded-[22px] p-3.5" style={{ background: '#000' }}>
            <span className="mx-3 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full font-mono text-[13px] font-extrabold text-black" style={{ background: s.tint }}>
              {s.n}
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="font-mono text-[12px] font-extrabold tracking-[0.14em] text-white">{s.title}</span>
              <span className="text-[13px] leading-[1.45]" style={{ color: '#8A8A8A' }}>{s.body}</span>
            </span>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onPrimary}
        className="pk-btn pk-primary-on-black h-14 font-mono text-[12px] font-extrabold tracking-[0.16em]"
      >
        {hasPhoto ? 'KEEP GOING →' : notice.primaryLabel}
      </button>

      <div className="flex flex-col items-center gap-2.5">
        <button type="button" onClick={onSecondary} className="pk-btn pk-text-on-black h-10 px-4.5 font-mono text-[11px] font-bold tracking-[0.16em]">
          {notice.secondaryLabel}
        </button>
        <span className="text-center font-mono text-[10px] tracking-[0.12em]" style={{ color: '#6B6B6B' }}>
          TWO MINUTES, TOPS. YOU CAN EDIT ANY OF IT LATER.
        </span>
      </div>
    </div>
  );
}
